import { RootState } from '../types';
import {
  PrimeArtifactState,
  PrimeArtifactStateTransition,
  PrimeProcess,
} from '../../types/primeModel';
import { PrimeArtifact } from '../../types/primeArtifact';
import { createSelector } from '@reduxjs/toolkit';

// Basic selectors
export const selectPrimeArtifacts = createSelector(
  (state: RootState) => state.primeArtifacts.primeArtifacts,
  (artifacts) => [...artifacts]
);

export const selectFlatArtifacts = (state: RootState): PrimeArtifact[] =>
  state.primeArtifacts.flatArtifacts;

export const selectPrimeArtifactStates = (state: RootState): PrimeArtifactState[] =>
  state.primeArtifacts.primeArtifactStates;

export const selectPrimeArtifactTransitions = (state: RootState): PrimeArtifactStateTransition[] =>
  state.primeArtifacts.primeArtifactTransitions;

export const selectPrimeProcesses = (state: RootState): PrimeProcess[] =>
  state.primeArtifacts.primeProcesses;

export const selectLoading = (state: RootState): boolean => state.primeArtifacts.loading;

export const selectError = (state: RootState): string | null => state.primeArtifacts.error;

export const selectHierarchyLoading = (state: RootState): boolean =>
  state.primeArtifacts.hierarchyLoading;

export const selectHierarchyError = (state: RootState): string | null =>
  state.primeArtifacts.hierarchyError;

// Derived selectors
export const selectPrimeArtifactById = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifacts,
    (_, artifactId: string) => artifactId,
  ],
  (artifacts, artifactId) => artifacts.find((artifact) => artifact.id === artifactId)
);

export const selectStatesByArtifactId = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactStates,
    (_, artifactId: string) => artifactId,
  ],
  (states, artifactId) => {
    console.log('[selectStatesByArtifactId] Filtering states for artifact:', artifactId);
    console.log('[selectStatesByArtifactId] Available states:', states);
    const filteredStates = states.filter((state) => state.prime_artifact_ref === artifactId);
    console.log('[selectStatesByArtifactId] Filtered states:', filteredStates);
    return filteredStates;
  }
);

export const selectTransitionsByArtifactId = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
    (_, artifactId: string) => artifactId,
  ],
  (transitions, artifactId) =>
    transitions.filter((transition) => transition.prime_artifact_ref === artifactId)
);

export const selectProcessesByArtifactId = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeProcesses,
    (_, artifactId: string) => artifactId,
  ],
  (processes, artifactId) =>
    processes.filter((process) => process.prime_artifact_ref === artifactId)
);

export const selectCreationState = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactStates,
    (_, artifactId: string) => artifactId,
  ],
  (states, artifactId) =>
    states.find((state) => state.prime_artifact_ref === artifactId && state.is_initial)
);

export const selectActiveState = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactStates,
    (_, artifactId: string) => artifactId,
  ],
  (states, artifactId) =>
    states.find(
      (state) => state.prime_artifact_ref === artifactId && !state.is_final && !state.is_initial
    )
);

export const selectConsumptionState = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactStates,
    (_, artifactId: string) => artifactId,
  ],
  (states, artifactId) =>
    states.find((state) => state.prime_artifact_ref === artifactId && state.is_final)
);

export const selectTransitionsFromState = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
    (_, stateId: string) => stateId,
  ],
  (transitions, stateId) =>
    transitions.filter((transition) => transition.from_state_ref === stateId)
);

export const selectTransitionsToState = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
    (_, stateId: string) => stateId,
  ],
  (transitions, stateId) => transitions.filter((transition) => transition.to_state_ref === stateId)
);

export const selectProcessByTransitionId = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
    (state: RootState) => state.primeArtifacts.primeProcesses,
    (_, transitionId: string) => transitionId,
  ],
  (transitions, processes, transitionId) => {
    const transition = transitions.find((t) => t.id === transitionId);
    if (!transition?.prime_process_ref) return undefined;
    return processes.find((p) => p.id === transition.prime_process_ref);
  }
);

export const selectInternalTransitionsByArtifactId = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
    (state: RootState) => state.primeArtifacts.primeArtifactStates,
    (_, artifactId: string) => artifactId,
  ],
  (transitions, states, artifactId) =>
    transitions.filter((transition) => {
      const fromState = states.find((s) => s.id === transition.from_state_ref);
      const toState = states.find((s) => s.id === transition.to_state_ref);
      return (
        fromState?.prime_artifact_ref === artifactId && toState?.prime_artifact_ref === artifactId
      );
    })
);

export const selectProcessTransitionsByArtifactId = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
    (state: RootState) => state.primeArtifacts.primeArtifactStates,
    (_, artifactId: string) => artifactId,
  ],
  (transitions, states, artifactId) =>
    transitions.filter((transition) => {
      const fromState = states.find((s) => s.id === transition.from_state_ref);
      const toState = states.find((s) => s.id === transition.to_state_ref);
      return (
        (fromState?.prime_artifact_ref === artifactId ||
          toState?.prime_artifact_ref === artifactId) &&
        transition.prime_process_ref
      );
    })
);

export const selectPrimeArtifactStatesById = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactStates,
    (_, artifactId: string) => artifactId,
  ],
  (states, artifactId) =>
    states.filter((state: PrimeArtifactState) => state.prime_artifact_ref === artifactId)
);

export const selectPrimeArtifactTransitionsById = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeArtifactTransitions,
    (_, artifactId: string) => artifactId,
  ],
  (transitions, artifactId) =>
    transitions.filter(
      (transition: PrimeArtifactStateTransition) => transition.prime_artifact_ref === artifactId
    )
);

export const selectPrimeProcessesById = createSelector(
  [
    (state: RootState) => state.primeArtifacts.primeProcesses,
    (_, artifactId: string) => artifactId,
  ],
  (processes, artifactId) =>
    processes.filter((process: PrimeProcess) => process.prime_artifact_ref === artifactId)
);
